import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { SOUND_VARIANTS, SoundType, pickSoundVariant } from '../utils/soundMap';
import { HapticCue, HapticImpactStyle, resolveHapticPattern } from '../utils/hapticMap';
import {
  HAPTIC_STORAGE_KEY,
  SOUND_STORAGE_KEY,
  readStoredHapticPreference,
  readStoredSoundPreference,
} from './soundPreferences';

// Prosty kontekst audio z lokalną preferencją, cooldownem i odblokowaniem AudioContext na gest.

interface SoundContextValue {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  toggleSound: () => void;
  hapticsEnabled: boolean;
  setHapticsEnabled: (enabled: boolean) => void;
  toggleHaptics: () => void;
  playHaptic: (cue: HapticCue, options?: { source?: string }) => void;
  playSound: (type: SoundType) => void;
}

const COOLDOWN_MS = 120;
const HAPTIC_COOLDOWN_MS = 40;
const DEFAULT_POOL_SIZE = 2;
const DICE_ROLL_POOL_SIZE = 4;

type AudioRuntimeStatus = 'locked' | 'suspended' | 'ready';
type AudioAppState = 'unknown' | 'active' | 'background';
type AudioRecoveryReason =
  | 'gesture'
  | 'visibility'
  | 'focus'
  | 'pageshow'
  | 'app-resume'
  | 'enable-sound'
  | 'play-request'
  | 'play-retry'
  | 'play-failure'
  | 'background'
  | 'hidden';

type AudioRuntimeState = {
  status: AudioRuntimeStatus;
  lastUnlockAt: number | null;
  lastResumeAt: number | null;
  lastForegroundAt: number | null;
  lastRecoveryReason: AudioRecoveryReason | null;
  lastAppState: AudioAppState;
  needsRecovery: boolean;
  poolsDirty: boolean;
};

const INITIAL_AUDIO_RUNTIME_STATE: AudioRuntimeState = {
  status: 'locked',
  lastUnlockAt: null,
  lastResumeAt: null,
  lastForegroundAt: null,
  lastRecoveryReason: null,
  lastAppState: 'unknown',
  needsRecovery: false,
  poolsDirty: false,
};

const SoundContext = React.createContext<SoundContextValue | undefined>(undefined);

const toImpactStyle = (style: HapticImpactStyle): ImpactStyle => {
  if (style === 'LIGHT') return ImpactStyle.Light;
  if (style === 'MEDIUM') return ImpactStyle.Medium;
  return ImpactStyle.Heavy;
};

const getPlatformName = (): string => {
  try {
    return Capacitor.getPlatform?.() || 'web';
  } catch {
    return 'web';
  }
};

const getVisibilityState = (): string => {
  if (typeof document === 'undefined') return 'unknown';
  return document.visibilityState || 'unknown';
};

const classifyContextStatus = (state?: string | null): AudioRuntimeStatus => {
  if (state === 'running') return 'ready';
  if (state === 'suspended' || state === 'interrupted' || state === 'closed') return 'suspended';
  return 'locked';
};

export function SoundProvider({ children }: { children: React.ReactNode }) {
  const [soundEnabled, setSoundEnabledState] = React.useState<boolean>(() => readStoredSoundPreference());
  const [hapticsEnabled, setHapticsEnabledState] = React.useState<boolean>(() => readStoredHapticPreference());
  const soundEnabledRef = React.useRef(soundEnabled);
  const lastPlayedRef = React.useRef<Partial<Record<SoundType, number>>>({});
  const lastHapticRef = React.useRef<Partial<Record<HapticCue, number>>>({});
  const audioCtxRef = React.useRef<AudioContext | null>(null);
  const hapticTimersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const nativeHapticUnavailableRef = React.useRef(false);
  const audioPoolsRef = React.useRef<Map<string, HTMLAudioElement[]>>(new Map());
  const audioPoolCursorRef = React.useRef<Map<string, number>>(new Map());
  const audioRuntimeRef = React.useRef<AudioRuntimeState>(INITIAL_AUDIO_RUNTIME_STATE);

  React.useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const persist = React.useCallback((next: boolean) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage?.setItem(SOUND_STORAGE_KEY, next ? 'true' : 'false');
    } catch {
      // ignore persistence errors
    }
  }, []);

  const persistHaptics = React.useCallback((next: boolean) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage?.setItem(HAPTIC_STORAGE_KEY, next ? 'true' : 'false');
    } catch {
      // ignore persistence errors
    }
  }, []);

  const ensureAudioContext = React.useCallback(() => {
    if (typeof window === 'undefined') return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new Ctx();
      } catch {
        audioCtxRef.current = null;
      }
    }
    return audioCtxRef.current;
  }, []);

  const updateAudioRuntime = React.useCallback((patch: Partial<AudioRuntimeState>) => {
    audioRuntimeRef.current = {
      ...audioRuntimeRef.current,
      ...patch,
    };
  }, []);

  const markAudioRuntimeDirty = React.useCallback((reason: AudioRecoveryReason) => {
    const nextStatus = reason === 'background' || reason === 'hidden'
      ? 'suspended'
      : classifyContextStatus((audioCtxRef.current?.state as string | undefined) ?? null);
    updateAudioRuntime({
      status: nextStatus,
      needsRecovery: true,
      poolsDirty: true,
      lastRecoveryReason: reason,
      lastAppState: reason === 'background' ? 'background' : audioRuntimeRef.current.lastAppState,
    });
  }, [updateAudioRuntime]);

  const resumeAudioContext = React.useCallback(async (reason: AudioRecoveryReason) => {
    if (typeof window === 'undefined') return true;
    const ctx = ensureAudioContext();
    if (!ctx) {
      updateAudioRuntime({
        status: 'ready',
        lastResumeAt: Date.now(),
        lastRecoveryReason: reason,
      });
      return true;
    }

    let activeContext = ctx;
    if ((activeContext.state as string | undefined) === 'closed') {
      audioCtxRef.current = null;
      const recreated = ensureAudioContext();
      if (!recreated) {
        updateAudioRuntime({
          status: 'locked',
          lastRecoveryReason: reason,
        });
        return false;
      }
      activeContext = recreated;
    }

    if ((activeContext.state as string | undefined) !== 'running') {
      try {
        await activeContext.resume();
      } catch {
        updateAudioRuntime({
          status: classifyContextStatus((activeContext.state as string | undefined) ?? null),
          lastRecoveryReason: reason,
        });
        return false;
      }
    }

    updateAudioRuntime({
      status: classifyContextStatus((activeContext.state as string | undefined) ?? null),
      lastResumeAt: Date.now(),
      lastRecoveryReason: reason,
    });
    return (activeContext.state as string | undefined) === 'running';
  }, [ensureAudioContext, updateAudioRuntime]);

  const setSoundEnabled = React.useCallback((next: boolean) => {
    soundEnabledRef.current = next;
    setSoundEnabledState(() => {
      persist(next);
      return next;
    });
    if (next) {
      updateAudioRuntime({
        lastUnlockAt: Date.now(),
        lastRecoveryReason: 'enable-sound',
      });
      void resumeAudioContext('enable-sound');
    }
  }, [persist, resumeAudioContext, updateAudioRuntime]);

  const toggleSound = React.useCallback(() => {
    setSoundEnabledState((prev) => {
      const next = !prev;
      soundEnabledRef.current = next;
      persist(next);
      if (next) {
        updateAudioRuntime({
          lastUnlockAt: Date.now(),
          lastRecoveryReason: 'enable-sound',
        });
        void resumeAudioContext('enable-sound');
      }
      return next;
    });
  }, [persist, resumeAudioContext, updateAudioRuntime]);

  const setHapticsEnabled = React.useCallback((next: boolean) => {
    setHapticsEnabledState(() => {
      persistHaptics(next);
      return next;
    });
  }, [persistHaptics]);

  const toggleHaptics = React.useCallback(() => {
    setHapticsEnabledState((prev) => {
      const next = !prev;
      persistHaptics(next);
      return next;
    });
  }, [persistHaptics]);

  const poolSizeFor = React.useCallback((url: string): number => {
    return url.includes('/sfx/dice-roll/') ? DICE_ROLL_POOL_SIZE : DEFAULT_POOL_SIZE;
  }, []);

  const ensureAudioPool = React.useCallback((url: string): HTMLAudioElement[] => {
    const existing = audioPoolsRef.current.get(url);
    if (existing) return existing;
    const size = poolSizeFor(url);
    const pool: HTMLAudioElement[] = Array.from({ length: size }, () => {
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.volume = 0.9;
      try {
        audio.load?.();
      } catch {
        // ignore eager load failures
      }
      return audio;
    });
    audioPoolsRef.current.set(url, pool);
    audioPoolCursorRef.current.set(url, 0);
    return pool;
  }, [poolSizeFor]);

  const createAudioElement = React.useCallback((url: string): HTMLAudioElement => {
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.volume = 0.9;
    try {
      audio.load?.();
    } catch {
      // ignore eager load failures
    }
    return audio;
  }, []);

  const takeAudioFromPool = React.useCallback((url: string): HTMLAudioElement => {
    const pool = ensureAudioPool(url);
    if (pool.length === 0) {
      return createAudioElement(url);
    }
    const cursor = audioPoolCursorRef.current.get(url) ?? 0;
    const index = cursor % pool.length;
    audioPoolCursorRef.current.set(url, (index + 1) % pool.length);
    return pool[index];
  }, [createAudioElement, ensureAudioPool]);

  const teardownAudioPools = React.useCallback(() => {
    audioPoolsRef.current.forEach((pool) => {
      pool.forEach((audio) => {
        try { audio.pause?.(); } catch {}
        try { audio.removeAttribute?.('src'); } catch {}
        try { audio.load?.(); } catch {}
      });
    });
    audioPoolsRef.current.clear();
    audioPoolCursorRef.current.clear();
  }, []);

  const recoverAudioRuntime = React.useCallback(async (
    reason: AudioRecoveryReason,
    options?: { userGesture?: boolean; rebuildPools?: boolean },
  ) => {
    const now = Date.now();
    const hasUnlockedAudio = audioRuntimeRef.current.lastUnlockAt !== null;
    const isForegroundRecovery = reason === 'app-resume'
      || reason === 'visibility'
      || reason === 'focus'
      || reason === 'pageshow';
    const shouldResetCooldowns = audioRuntimeRef.current.needsRecovery
      || reason === 'app-resume'
      || reason === 'visibility'
      || reason === 'focus'
      || reason === 'pageshow'
      || reason === 'play-retry';
    if (shouldResetCooldowns) {
      lastPlayedRef.current = {};
    }
    if (options?.rebuildPools || audioRuntimeRef.current.poolsDirty) {
      teardownAudioPools();
    }
    if (isForegroundRecovery && !options?.userGesture && !hasUnlockedAudio) {
      updateAudioRuntime({
        lastRecoveryReason: reason,
        lastForegroundAt: now,
        lastAppState: 'active',
        needsRecovery: true,
        poolsDirty: false,
      });
      return false;
    }
    const resumed = await resumeAudioContext(reason);
    updateAudioRuntime({
      status: resumed ? 'ready' : audioRuntimeRef.current.status,
      lastRecoveryReason: reason,
      lastUnlockAt: options?.userGesture ? now : audioRuntimeRef.current.lastUnlockAt,
      lastResumeAt: resumed ? now : audioRuntimeRef.current.lastResumeAt,
      lastForegroundAt: ['app-resume', 'visibility', 'focus', 'pageshow'].includes(reason)
        ? now
        : audioRuntimeRef.current.lastForegroundAt,
      lastAppState: ['app-resume', 'visibility', 'focus', 'pageshow', 'gesture', 'play-request', 'play-retry', 'enable-sound'].includes(reason)
        ? 'active'
        : audioRuntimeRef.current.lastAppState,
      needsRecovery: !resumed,
      poolsDirty: false,
    });
    return resumed;
  }, [resumeAudioContext, teardownAudioPools, updateAudioRuntime]);

  const clearScheduledHaptics = React.useCallback(() => {
    hapticTimersRef.current.forEach((timer) => clearTimeout(timer));
    hapticTimersRef.current = [];
  }, []);

  React.useEffect(() => {
    return () => {
      clearScheduledHaptics();
      teardownAudioPools();
    };
  }, [clearScheduledHaptics, teardownAudioPools]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const onUserInteraction = () => {
      void recoverAudioRuntime('gesture', { userGesture: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void recoverAudioRuntime('visibility', { rebuildPools: true });
        return;
      }
      markAudioRuntimeDirty('hidden');
    };
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void recoverAudioRuntime('focus', { rebuildPools: true });
    };
    const onPageShow = () => {
      void recoverAudioRuntime('pageshow', { rebuildPools: true });
    };

    window.addEventListener('pointerdown', onUserInteraction, { passive: true });
    window.addEventListener('touchend', onUserInteraction, { passive: true });
    window.addEventListener('keydown', onUserInteraction);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pointerdown', onUserInteraction);
      window.removeEventListener('touchend', onUserInteraction);
      window.removeEventListener('keydown', onUserInteraction);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [markAudioRuntimeDirty, recoverAudioRuntime]);

  React.useEffect(() => {
    if (!(Capacitor.isNativePlatform?.() ?? false)) return;
    let cancelled = false;
    let sub: PluginListenerHandle | null = null;

    const setup = async () => {
      try {
        sub = await CapApp.addListener('appStateChange', ({ isActive }) => {
          if (cancelled) return;
          if (isActive) {
            void recoverAudioRuntime('app-resume', { rebuildPools: true });
            return;
          }
          markAudioRuntimeDirty('background');
        });
      } catch {
        // ignore native lifecycle listener failures
      }
    };

    void setup();
    return () => {
      cancelled = true;
      if (sub) {
        void sub.remove();
      }
    };
  }, [markAudioRuntimeDirty, recoverAudioRuntime]);

  const canVibrate = React.useCallback(() => (
    typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'
  ), []);

  const triggerVibrate = React.useCallback((pattern: number[]) => {
    if (!canVibrate()) return;
    try {
      navigator.vibrate(pattern);
    } catch {
      // ignore vibrate API failures
    }
  }, [canVibrate]);

  const scheduleHapticPulse = React.useCallback((delayMs: number, fn: () => void) => {
    const timer = setTimeout(() => {
      hapticTimersRef.current = hapticTimersRef.current.filter((candidate) => candidate !== timer);
      fn();
    }, delayMs);
    hapticTimersRef.current.push(timer);
  }, []);

  const playHaptic = React.useCallback((cue: HapticCue, options?: { source?: string }) => {
    if (!hapticsEnabled) return;
    const now = Date.now();
    const last = lastHapticRef.current[cue] || 0;
    if (now - last < HAPTIC_COOLDOWN_MS) return;
    lastHapticRef.current[cue] = now;

    const pattern = resolveHapticPattern(cue, options?.source);
    if (!pattern.vibratePattern.length && !pattern.pulses.length) return;

    const isNative = Capacitor.isNativePlatform?.() ?? false;
    if (!isNative || nativeHapticUnavailableRef.current) {
      triggerVibrate(pattern.vibratePattern);
      return;
    }

    let fallbackTriggered = false;
    pattern.pulses.forEach((pulse) => {
      scheduleHapticPulse(pulse.atMs, () => {
        void Haptics.impact({ style: toImpactStyle(pulse.style) }).catch(() => {
          nativeHapticUnavailableRef.current = true;
          if (fallbackTriggered) return;
          fallbackTriggered = true;
          triggerVibrate(pattern.vibratePattern);
        });
      });
    });
  }, [hapticsEnabled, scheduleHapticPulse, triggerVibrate]);

  const appendSfxError = React.useCallback((payload: {
    type: SoundType;
    candidate: string;
    reason: string;
    attempt: 'pool' | 'fresh-retry';
    message?: string;
  }) => {
    const runtime = audioRuntimeRef.current;
    const errorPayload = {
      ts: Date.now(),
      type: payload.type,
      candidate: payload.candidate,
      reason: payload.reason,
      attempt: payload.attempt,
      message: payload.message,
      platform: getPlatformName(),
      visibilityState: getVisibilityState(),
      appState: runtime.lastAppState,
      runtimeStatus: runtime.status,
      needsRecovery: runtime.needsRecovery,
      poolsDirty: runtime.poolsDirty,
      lastUnlockAt: runtime.lastUnlockAt,
      lastResumeAt: runtime.lastResumeAt,
      lastRecoveryReason: runtime.lastRecoveryReason,
    };
    if (typeof window !== 'undefined') {
      const existing = Array.isArray(window.__SFX_ERRORS__) ? window.__SFX_ERRORS__ : [];
      // eslint-disable-next-line react-compiler/react-compiler -- intentional write to global debug diagnostic buffer
      window.__SFX_ERRORS__ = [...existing.slice(-19), errorPayload];
    }
    console.warn('SFX_PLAY_FAILED', errorPayload);
  }, []);

  const playAudioElement = React.useCallback(async (
    audio: HTMLAudioElement,
    meta: { type: SoundType; candidate: string; attempt: 'pool' | 'fresh-retry' },
  ): Promise<boolean> => {
    audio.volume = 0.9;
    try {
      if (audio.currentTime > 0) audio.currentTime = 0;
    } catch {
      // Ignore seek failures (some browsers block seek before metadata)
    }
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const onError = () => {
        const code = audio.error?.code;
        fail(code ? `error-code-${code}` : 'error-event', audio.error);
      };
      const cleanup = () => {
        try {
          audio.removeEventListener('error', onError);
        } catch {
          // ignore listener cleanup failures on mocked audio objects
        }
      };
      const succeed = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(true);
      };
      const fail = (reason: string, error?: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        appendSfxError({
          ...meta,
          reason,
          message: error instanceof Error ? error.message : (error ? String(error) : undefined),
        });
        resolve(false);
      };

      try {
        audio.addEventListener('error', onError, { once: true });
      } catch {
        // ignore mocked event listener failures
      }

      try {
        const playResult = audio.play();
        if (!playResult || typeof (playResult as Promise<void>).then !== 'function') {
          succeed();
          return;
        }
        void playResult.then(() => {
          succeed();
        }).catch((error) => {
          fail('play-rejected', error);
        });
      } catch (error) {
        fail('exception', error);
      }
    });
  }, [appendSfxError]);

  const playSound = React.useCallback((type: SoundType) => {
    const variants = SOUND_VARIANTS[type] || [];
    const src = pickSoundVariant(type);
    playHaptic(type, { source: src });
    if (!soundEnabledRef.current) return;
    if (typeof Audio === 'undefined') return;
    const now = Date.now();
    const last = lastPlayedRef.current[type] || 0;
    if (now - last < COOLDOWN_MS) return;
    lastPlayedRef.current[type] = now;
    const candidates = [src, ...variants.filter((variant) => variant !== src)];

    const playCandidates = async () => {
      await recoverAudioRuntime('play-request', { rebuildPools: audioRuntimeRef.current.poolsDirty });
      for (const candidate of candidates) {
        const pooledAudio = takeAudioFromPool(candidate);
        const pooledPlayed = await playAudioElement(pooledAudio, {
          type,
          candidate,
          attempt: 'pool',
        });
        if (pooledPlayed) return;

        markAudioRuntimeDirty('play-failure');
        await recoverAudioRuntime('play-retry', { rebuildPools: true });
        const retryAudio = createAudioElement(candidate);
        const retried = await playAudioElement(retryAudio, {
          type,
          candidate,
          attempt: 'fresh-retry',
        });
        if (retried) return;
      }
    };

    void playCandidates();
  }, [createAudioElement, markAudioRuntimeDirty, playAudioElement, playHaptic, recoverAudioRuntime, takeAudioFromPool]);

  const value = React.useMemo<SoundContextValue>(() => ({
    soundEnabled,
    setSoundEnabled,
    toggleSound,
    hapticsEnabled,
    setHapticsEnabled,
    toggleHaptics,
    playHaptic,
    playSound,
  }), [
    soundEnabled,
    setSoundEnabled,
    toggleSound,
    hapticsEnabled,
    setHapticsEnabled,
    toggleHaptics,
    playHaptic,
    playSound,
  ]);

  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound(): SoundContextValue {
  const ctx = React.useContext(SoundContext);
  if (!ctx) {
    throw new Error('useSound must be used within SoundProvider');
  }
  return ctx;
}
